import { useQuery } from '@hooks/reactQuery';

import { getGoldenSettingsConfig } from '@api/goldenSettings';

import { handleErrorFromMessage } from '@utils/handleRequestError';

import { TGoldenSettingsSetTypeDTO } from '@interfaces/cmModule/dto/goldenSettingsDTO';
import { IGoldenSettingsConfigData } from '@interfaces/cmModule/goldenSettings';
import { IResponseError } from '@interfaces/errors/apiErrors';

import { EVendorsValues } from '@enums/common';
import { EErrorCodes } from '@enums/errorCodes';

import { refetchOptions } from '@constants/common';
import { GOLDEN_SETTING } from '@constants/queryKeys';

interface IUseQueryGoldenSettingsConfigReturnType {
	goldenSettingsConfig?: IGoldenSettingsConfigData;
	isLoadingGoldenSettingsConfig: boolean;
}

const useQueryGoldenSettingsConfig = (
	params: Partial<TGoldenSettingsSetTypeDTO>,
): IUseQueryGoldenSettingsConfigReturnType => {
	const {
		vendor,
		technology,
		nodeType,
		nodes,
		type,
		version,
	} = params || {};

	const isMavenir = vendor === EVendorsValues.mavenir;

	const isEnabled = isMavenir
		? !!vendor && !!type && !!version
		: !!vendor && !!technology && !!nodeType;

	const dto = isMavenir
		? { vendor, type, version, nodes }
		: { vendor, technology, nodeType };

	const {
		data,
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: [
			GOLDEN_SETTING,
			vendor,
			technology,
			nodeType,
			version,
			type,
			nodes,
		],
		queryFn: () => getGoldenSettingsConfig(dto),
		onError: (err: IResponseError) => {
			if (!err.errors?.find((item) => item.code === EErrorCodes.goldenSettingsNotSet)) {
				handleErrorFromMessage(err);
			}
		},
		enabled: [isEnabled],
		...refetchOptions,
	});

	return {
		goldenSettingsConfig: data,
		isLoadingGoldenSettingsConfig: (isLoading && !!data) || isFetching,
	};
};

export default useQueryGoldenSettingsConfig;
